import {useState} from 'react'
import {Button, Dialog, Flash, Link} from '@primer/react'
import {announce} from '@github-ui/aria-live'
import {useDequeuePullRequestMutation} from '../../hooks/mutations/use-dequeue-pull-request-mutation'
import {useSidebarMergeData} from './use-sidebar-merge-data'

export function SidebarQueuedState() {
  const {mergeBoxPullRequest} = useSidebarMergeData()
  const mergeQueueEntry = mergeBoxPullRequest?.mergeQueueEntry
  const mergeQueue = mergeBoxPullRequest?.mergeQueue ?? null
  const viewerCanAddAndRemoveFromMergeQueue = mergeBoxPullRequest?.viewerCanAddAndRemoveFromMergeQueue ?? false

  const [isConfirmationDialogOpen, setIsConfirmationDialogOpen] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string>()
  const {mutate: dequeuePullRequest, isPending: isDequeuing} = useDequeuePullRequestMutation({
    onError: (e: Error) => {
      setIsConfirmationDialogOpen(false)
      setErrorMessage(e.message)
      announce(e.message)
    },
  })

  if (!mergeQueueEntry) return null

  const handleDequeue = () => {
    if (isDequeuing) return
    setErrorMessage(undefined)
    dequeuePullRequest(undefined, {
      onSuccess: () => setIsConfirmationDialogOpen(false),
    })
  }

  const handleDismiss = () => {
    if (!isDequeuing) setIsConfirmationDialogOpen(false)
  }

  const pullsAhead = mergeQueueEntry.position - 1
  const queuePositionPrefix =
    pullsAhead === 0
      ? 'This pull request is next up in the '
      : `There ${pullsAhead === 1 ? 'is 1 pull request' : `are ${pullsAhead} pull requests`} ahead in the `

  return (
    <div className="d-flex flex-column gap-2 pt-2">
      {errorMessage && <Flash variant="danger">{errorMessage}</Flash>}
      {viewerCanAddAndRemoveFromMergeQueue && !mergeQueueEntry.isLocked && (
        <Button block loading={isDequeuing} onClick={() => setIsConfirmationDialogOpen(true)}>
          Remove from queue
        </Button>
      )}
      {isConfirmationDialogOpen && (
        <Dialog
          title="Remove from the queue?"
          role="alertdialog"
          onClose={handleDismiss}
          footerButtons={[
            {content: 'Cancel', buttonType: 'default', onClick: handleDismiss, disabled: isDequeuing, autoFocus: true},
            {
              content: 'Remove from the queue',
              buttonType: 'danger',
              onClick: handleDequeue,
              loading: isDequeuing,
              loadingAnnouncement: 'Removing from the queue',
            },
          ]}
        >
          Removing this pull request from the queue could impact other pull requests in the queue. Are you sure?
        </Dialog>
      )}
      <span className="text-small color-fg-muted">
        {mergeQueue ? (
          <>
            <span>{queuePositionPrefix}</span>
            <Link href={mergeQueue.url} inline>
              merge queue
            </Link>
            <span>.</span>
          </>
        ) : (
          `${queuePositionPrefix}merge queue.`
        )}
      </span>
    </div>
  )
}
